import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { 
  ArrowLeft, MessageSquare, Inbox as InboxIcon, Calendar, 
  Loader2, ChevronRight, User, Clock, CheckCircle2, Plus,
  Mail, Send, AlertCircle, ChevronLeft, UserPlus
} from 'lucide-react';
import CreditPaywall from '../components/CreditPaywall';
import LanguageSelector from '../components/LanguageSelector';
import CreditBalance from '../components/CreditBalance';
import GuestSignupBanner from '../components/GuestSignupBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inbox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeaders, refreshCreditBalance, isGuestMode } = useAuth();
  
  // Thread list state
  const [emailThreads, setEmailThreads] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('conversations'); // 'conversations' (primary) or 'messages'
  
  // Compose modal state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeToName, setComposeToName] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);
  
  // Thread detail state
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Credit paywall state
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallAction, setPaywallAction] = useState('send a message');
  const [paywallCurrentCount, setPaywallCurrentCount] = useState(null);
  const [paywallFreeLimit, setPaywallFreeLimit] = useState(null);

  // Check URL params for prefill
  useEffect(() => {
    const toEmail = searchParams.get('to');
    const toName = searchParams.get('name');
    if (toEmail) {
      setComposeTo(toEmail);
      setComposeToName(toName || '');
      setComposeOpen(true);
    }
  }, [searchParams]);

  // Fetch email threads
  const fetchEmailThreads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email-threads`, {
        headers: getAuthHeaders()
      });
      setEmailThreads(response.data);
    } catch (error) {
      console.error('Failed to fetch email threads:', error);
    }
  }, [getAuthHeaders]);

  // Fetch hold conflict conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/conversations`, {
        headers: getAuthHeaders()
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, [getAuthHeaders]);

  // Load all data
  useEffect(() => {
    if (isGuestMode) {
      setLoading(false);
      return;
    }
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEmailThreads(), fetchConversations()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEmailThreads, fetchConversations, isGuestMode]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Sent':
        return (
          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Sent
          </Badge>
        );
      case 'Failed':
        return (
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case 'Draft':
      default:
        return (
          <Badge variant="outline" className="text-xs bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-900/20 dark:text-gray-400 dark:border-gray-800">
            Draft
          </Badge>
        );
    }
  };

  // Handle compose submit
  const handleSendMessage = async () => {
    if (!composeTo.trim() || !composeBody.trim()) {
      toast.error('Email and message are required');
      return;
    }

    // Simple email validation
    if (!composeTo.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setSending(true);
      const response = await axios.post(`${API}/email-threads`, {
        to_email: composeTo.trim(),
        to_name: composeToName.trim() || null,
        subject: composeSubject.trim() || null,
        body: composeBody.trim()
      }, {
        headers: getAuthHeaders()
      });

      if (response.data.sendStatus === 'Sent') {
        toast.success('Message sent successfully!');
        // Refresh credit balance after successful send
        await refreshCreditBalance();
      } else if (response.data.sendStatus === 'Failed') {
        toast.error(`Failed to send: ${response.data.errorText || 'Unknown error'}`);
      }

      // Reset form and close modal
      setComposeTo('');
      setComposeToName('');
      setComposeSubject('');
      setComposeBody('');
      setComposeOpen(false);
      
      // Refresh threads
      await fetchEmailThreads();
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Handle insufficient credits (402 Payment Required)
      if (error.response?.status === 402) {
        const detail = error.response.data?.detail || {};
        setPaywallAction('send a message');
        setPaywallCurrentCount(detail.current_count ?? null);
        setPaywallFreeLimit(detail.free_limit ?? null);
        setPaywallOpen(true);
        return;
      }
      
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Open thread detail
  const handleOpenThread = async (thread) => {
    setSelectedThread(thread);
    setThreadLoading(true);
    setThreadMessages([]);
    setReplyBody('');

    try {
      const response = await axios.get(`${API}/email-threads/${thread.id}`, {
        headers: getAuthHeaders()
      });
      setThreadMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to load thread:', error);
      toast.error('Failed to load messages');
    } finally {
      setThreadLoading(false);
    }
  };

  // Handle reply in thread
  const handleSendReply = async () => {
    if (!replyBody.trim() || !selectedThread) return;

    try {
      setSendingReply(true);
      const response = await axios.post(`${API}/email-threads/${selectedThread.id}/messages`, {
        to_email: selectedThread.participantEmail,
        to_name: selectedThread.participantName,
        body: replyBody.trim()
      }, {
        headers: getAuthHeaders()
      });

      if (response.data.sendStatus === 'Sent') {
        toast.success('Reply sent!');
      } else if (response.data.sendStatus === 'Failed') {
        toast.error(`Failed to send: ${response.data.errorText || 'Unknown error'}`);
      }

      setReplyBody('');
      
      // Refresh thread messages
      const threadResponse = await axios.get(`${API}/email-threads/${selectedThread.id}`, {
        headers: getAuthHeaders()
      });
      setThreadMessages(threadResponse.data.messages);
      setSelectedThread(threadResponse.data.thread);
      
      // Refresh thread list
      await fetchEmailThreads();
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Get the other planner in a conversation
  const getOtherPlanner = (conv) => {
    if (conv.plannerAId === user?.id) {
      return {
        name: conv.plannerBName,
        username: conv.plannerBUsername,
        photo: conv.plannerBPhoto
      };
    }
    return {
      name: conv.plannerAName,
      username: conv.plannerAUsername,
      photo: conv.plannerAPhoto
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Thread Detail View
  if (selectedThread) {
    return (
      <div className="min-h-screen bg-background pb-20 flex flex-col">
        {/* Thread Header */}
        <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedThread(null)}
                className="h-8 w-8"
                data-testid="thread-back-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1 min-w-0">
                <h1 className="font-semibold truncate">
                  {selectedThread.participantName || selectedThread.participantEmail}
                </h1>
                <p className="text-xs text-muted-foreground truncate">
                  {selectedThread.participantEmail}
                </p>
              </div>
              {getStatusBadge(selectedThread.status)}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
            {threadLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : threadMessages.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-8 text-center">
                  <Mail className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">No messages in this thread</p>
                </CardContent>
              </Card>
            ) : (
              threadMessages.map((msg) => (
                <Card key={msg.id} className={`${msg.sendStatus === 'Failed' ? 'border-red-300' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">To: {msg.toName || msg.toEmail}</span>
                      </div>
                      {getStatusBadge(msg.sendStatus)}
                    </div>
                    {msg.subject && (
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Subject: {msg.subject}
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-muted-foreground">
                        {formatTime(msg.createdAt)}
                      </span>
                      {msg.sendStatus === 'Failed' && msg.errorText && (
                        <span className="text-xs text-red-500">
                          Error: {msg.errorText}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Reply Input */}
        <div className="border-t bg-card sticky bottom-0">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <div className="space-y-2">
              <Textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Type your reply..."
                className="min-h-[80px] resize-none"
                data-testid="reply-input"
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleSendReply}
                  disabled={!replyBody.trim() || sendingReply}
                  data-testid="send-reply-btn"
                >
                  {sendingReply ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Reply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Inbox View
  return (
    <div className="min-h-screen bg-background pb-20">
      <GuestSignupBanner />
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/hub')}
                className="h-8 w-8"
                data-testid="inbox-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <InboxIcon className="h-5 w-5 text-primary" />
                Inbox
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector variant="icon" />
              <Button 
                size="sm" 
                onClick={() => setComposeOpen(true)}
                data-testid="new-message-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                New Message
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Invite Planners Card */}
        <Card className="mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200" data-testid="inbox-invite-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2.5 bg-emerald-100 dark:bg-emerald-800/40 rounded-xl shrink-0">
              <UserPlus className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-emerald-800 dark:text-emerald-200">Invite Planners</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">Earn credits by inviting others. Share your referral code!</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              onClick={() => navigate('/account?tab=referrals')}
              data-testid="inbox-invite-btn"
            >
              Invite
            </Button>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="conversations" className="flex items-center gap-2" data-testid="conversations-tab">
              <MessageSquare className="h-4 w-4" />
              Hold Talks ({conversations.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2" data-testid="messages-tab">
              <Mail className="h-4 w-4" />
              Email History ({emailThreads.length})
            </TabsTrigger>
          </TabsList>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-4">
            {emailThreads.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Mail className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No messages yet</h3>
                  <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-4">
                    Send messages to other planners or contacts via email. Messages are sent from a no-reply address on your behalf.
                  </p>
                  <Button 
                    onClick={() => setComposeOpen(true)}
                    data-testid="empty-new-message-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Send Your First Message
                  </Button>
                  <div className="mt-6 pt-4 border-t border-dashed">
                    <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-1" data-testid="inbox-referral-promo">
                      Earn credits by inviting other planners to join!
                    </p>
                    <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                      Share your referral code from Account &gt; Referrals to earn credits when they sign up.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {emailThreads.map((thread) => (
                  <Card 
                    key={thread.id}
                    className={`cursor-pointer hover:shadow-md transition-all border-l-4 ${
                      thread.status === 'Sent' 
                        ? 'border-l-green-500 hover:border-l-green-600'
                        : thread.status === 'Failed'
                        ? 'border-l-red-500 hover:border-l-red-600'
                        : 'border-l-gray-400 hover:border-l-gray-500'
                    }`}
                    onClick={() => handleOpenThread(thread)}
                    data-testid={`thread-${thread.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-semibold truncate">
                              {thread.participantName || thread.participantEmail}
                            </h3>
                            {getStatusBadge(thread.status)}
                          </div>
                          
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {thread.lastMessagePreview || 'No message content'}
                          </p>
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {thread.participantEmail}
                            </span>
                            {thread.lastMessageAt && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatTime(thread.lastMessageAt)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Conversations Tab (Hold Talks) */}
          <TabsContent value="conversations" className="mt-4">
            {conversations.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No hold conversations yet</h3>
                  <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                    Conversations appear here when you coordinate with other planners on overlapping holds or events.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => {
                  const otherPlanner = getOtherPlanner(conv);
                  const isOpen = conv.status === 'Open';
                  
                  return (
                    <Card 
                      key={conv.id}
                      className={`cursor-pointer hover:shadow-md transition-all border-l-4 ${
                        isOpen 
                          ? 'border-l-blue-500 hover:border-l-blue-600' 
                          : 'border-l-green-500 hover:border-l-green-600'
                      }`}
                      onClick={() => navigate(`/conversations/${conv.id}`)}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {otherPlanner.photo ? (
                              <img 
                                src={otherPlanner.photo} 
                                alt={otherPlanner.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-5 w-5 text-primary" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="font-semibold truncate">
                                {otherPlanner.name}
                              </h3>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isOpen ? (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800">
                                    Active
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Resolved
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            <p className="text-sm text-muted-foreground truncate mt-0.5">
                              {conv.eventTitle}
                            </p>
                            
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {conv.holdDay}
                              </span>
                              {(conv.lastMessageAt || conv.updatedAt) && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTime(conv.lastMessageAt || conv.updatedAt)}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info Footer */}
        {(emailThreads.length > 0 || conversations.length > 0) && (
          <p className="text-xs text-muted-foreground text-center mt-6">
            {activeTab === 'messages' 
              ? `${emailThreads.length} message thread${emailThreads.length !== 1 ? 's' : ''}`
              : `${conversations.length} conversation${conversations.length !== 1 ? 's' : ''}`
            }
          </p>
        )}
      </div>

      {/* Compose Modal */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              New Message
            </DialogTitle>
            <DialogDescription>
              Send an email message to a planner or contact.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="compose-to">To (Email) *</Label>
              <Input
                id="compose-to"
                type="email"
                placeholder="recipient@example.com"
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                data-testid="compose-to-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="compose-name">Name (Optional)</Label>
              <Input
                id="compose-name"
                placeholder="Recipient name"
                value={composeToName}
                onChange={(e) => setComposeToName(e.target.value)}
                data-testid="compose-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="compose-subject">Subject (Optional)</Label>
              <Input
                id="compose-subject"
                placeholder="Message subject"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                data-testid="compose-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="compose-body">Message *</Label>
              <Textarea
                id="compose-body"
                placeholder="Type your message..."
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                className="min-h-[120px] resize-none"
                data-testid="compose-body-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setComposeOpen(false)}
              data-testid="compose-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendMessage}
              disabled={!composeTo.trim() || !composeBody.trim() || sending}
              data-testid="compose-send-btn"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Send Message (1 credit)</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Paywall Modal */}
      <CreditPaywall
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        requiredCredits={1}
        actionName={paywallAction}
        currentCount={paywallCurrentCount}
        freeLimit={paywallFreeLimit}
        onPurchase={(pkg) => {
          // For now, just show a toast - payment integration would go here
          toast.info(`Credits purchase coming soon! Selected: ${pkg.amount} credits for $${pkg.price}`);
          setPaywallOpen(false);
        }}
      />
    </div>
  );
};

export default Inbox;
